import React from 'react';
import { Building, Users, Key, Shield, Clock, CreditCard, Lightbulb, Share2 } from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const EnterpriseGuide = () => {
  return (
    <div className="font-sans antialiased text-gray-900">
      <MainNavbar activePage="help" />
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-lg mb-8">
          <div className="flex items-start">
            <Building className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
            <div>
              <h1 className="text-2xl font-bold mb-3">Enterprise Plan Guide</h1>
              <p className="text-gray-700">
                Understand how to set up, manage, and utilize enterprise accounts for team access. Learn about admin 
                capabilities, member management, seat allocation, and best practices for maximizing the value of your 
                enterprise subscription.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Building className="w-5 h-5 mr-2 text-gray-700" />
              Understanding Enterprise Accounts
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Enterprise accounts provide organizations with a centralized way to manage team access:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Centralized team management</strong> - A single admin can control access for the entire organization.
                </li>
                <li>
                  <strong>Seat-based licensing</strong> - Purchase access for a specific number of team members (e.g., 5, 10, 25, 50, or 100 seats).
                </li>
                <li>
                  <strong>Annual access</strong> - Enterprise plans provide 365 days of access from the purchase date.
                </li>
                <li>
                  <strong>Simplified billing</strong> - Pay once for your entire team, with no individual subscription management.
                </li>
              </ul>
              <p className="mt-4">
                Enterprise accounts are ideal for design teams, product departments, agencies, or any organization 
                where multiple people need access to prototyping tools.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <CreditCard className="w-5 h-5 mr-2 text-gray-700" />
              Creating an Enterprise Account
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Setting up an enterprise account is straightforward:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Navigate to your account settings</strong> and select "Enterprise" from the tabs.
                </li>
                <li>
                  <strong>Click "Create Enterprise Account"</strong> to start the setup process.
                </li>
                <li>
                  <strong>Enter your organization details</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li><strong>Organization Name</strong> - The name that will appear to all team members</li>
                    <li><strong>Number of Seats</strong> - Select how many team members need access</li>
                  </ul>
                </li>
                <li>
                  <strong>Review pricing</strong> based on your seat selection.
                </li>
                <li>
                  <strong>Complete payment</strong> through our secure checkout system.
                </li>
              </ol>
              <p className="mt-4">
                After successful payment, you'll be automatically designated as the enterprise admin 
                and will receive your enterprise key to share with team members.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Users className="w-5 h-5 mr-2 text-gray-700" />
              Managing Team Members
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                As an enterprise admin, you have powerful tools to manage your team's access:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Invite team members</strong> by sharing your enterprise key with them.
                </li>
                <li>
                  <strong>Monitor seat usage</strong> through the enterprise dashboard to see how many seats are in use.
                </li>
                <li>
                  <strong>View team member details</strong> including email addresses and join dates.
                </li>
                <li>
                  <strong>Revoke access</strong> for team members who no longer need it, freeing up seats for new members.
                </li>
                <li>
                  <strong>Track enterprise status</strong> including expiration date and active/expired status.
                </li>
              </ul>
              <p className="mt-4">
                The enterprise dashboard provides a comprehensive view of your team's access status, 
                making it easy to manage who has access and when changes are needed.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Key className="w-5 h-5 mr-2 text-gray-700" />
              Enterprise Key Management
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                The enterprise key is how team members join your enterprise account:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Unique enterprise key</strong> - A secure 16-digit code formatted as XXXX-XXXX-XXXX-XXXX.
                </li>
                <li>
                  <strong>Key distribution</strong> - Share this key securely with your team members.
                </li>
                <li>
                  <strong>User join process</strong>:
                  <ol className="list-decimal pl-5 mt-2 space-y-1">
                    <li>Team members navigate to the "Enterprise" tab in their account settings</li>
                    <li>They select "Join Enterprise" and enter the enterprise key</li>
                    <li>Once verified, they immediately gain access to all features</li>
                  </ol>
                </li>
                <li>
                  <strong>Key regeneration</strong> - As an admin, you can regenerate the enterprise key if needed:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>This creates a new key and invalidates the old one</li>
                    <li>Existing members remain in the enterprise</li>
                    <li>New members will need the new key to join</li>
                  </ul>
                </li>
              </ul>
              <p className="mt-4">
                For security, treat your enterprise key like a password. Regenerate it if you believe it has been 
                shared with unauthorized individuals or if you want to prevent new members from joining.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Shield className="w-5 h-5 mr-2 text-gray-700" />
              Access and Permissions
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Enterprise accounts have two distinct roles:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Enterprise Admin</strong> - The person who created the enterprise account*:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Can view all team members</li>
                    <li>Can revoke access for members</li>
                    <li>Can regenerate the enterprise key</li>
                    <li>Has full access to all product features</li>
                    <li>Cannot be removed from the enterprise</li>
                    <li>Can access and download all enterprise member's data</li>
                  </ul>
                </li>
                <li>
                  <strong>Enterprise Member</strong> - Team members who join using the enterprise key:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Have full access to all product features</li>
                    <li>Cannot see or manage other team members</li>
                    <li>Cannot revoke others' access or regenerate the key</li>
                    <li>Can leave the enterprise voluntarily</li>
                  </ul>
                </li>
              </ul>

              <p className="mt-4">
                All team members, regardless of role, have identical access to the product features. 
                The difference lies in administrative capabilities.
              </p>
              <p className="mt-4">
                * The enterprise admin is the person who initially created the enterprise account by purchasing the plan. However, the organization can change the enterprise admin at any time by contacting support.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Share2 className="w-5 h-5 mr-2 text-gray-700" />
              Enterprise Sharing and Collaboration
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                As an enterprise member, you can securely share and collaborate on designs exclusively 
                within your organization. Here's how to use enterprise sharing effectively:
              </p>
              
              <h3 className="font-semibold mb-3 text-lg">How to Share Content with Your Team</h3>
              <ul className="list-disc pl-5 space-y-3 mb-6">
                <li>
                  <strong>Open the share modal</strong> - Click the share button on any view or flow you want to share.
                </li>
                <li>
                  <strong>Switch to Enterprise tab</strong> - Select the "Enterprise" tab in the sharing modal 
                  (only available if you're part of an enterprise account).
                </li>
                <li>
                  <strong>Select team members</strong> - Search for and select teammates by typing their email 
                  addresses. Only members of your enterprise will appear in the search results.
                </li>
                <li>
                  <strong>Share instantly</strong> - Click "Share" to immediately give selected team members 
                  access. Enterprise shares never expire and don't require passwords.
                </li>
              </ul>

              <h3 className="font-semibold mb-3 text-lg">Finding Content Shared with You</h3>
              <ul className="list-disc pl-5 space-y-3 mb-6">
                <li>
                  <strong>Access your shared items</strong> - Navigate to your "Shared with Me" page to see 
                  all content teammates have shared with you.
                </li>
                <li>
                  <strong>Search and filter</strong> - Use the search bar to find specific shared items, or 
                  sort by date or title to organize the list.
                </li>
                <li>
                  <strong>Check availability</strong> - Items show status indicators if the sharing enterprise 
                  has expired or if content is no longer available.
                </li>
                <li>
                  <strong>View shared content</strong> - Click "View" to open any shared item in presentation 
                  mode without affecting the original.
                </li>
              </ul>

              <h3 className="font-semibold mb-3 text-lg">Copying Shared Designs to Your Projects</h3>
              <ul className="list-disc pl-5 space-y-3 mb-6">
                <li>
                  <strong>Choose how to copy</strong> - Click "Copy" on any shared item to open the copy modal 
                  with two options: add to an existing project or create a new project.
                </li>
                <li>
                  <strong>Add to existing project</strong> - Select one of your current projects from the list. 
                  The copied view will automatically use your project's style settings.
                </li>
                <li>
                  <strong>Create new project</strong> - Enter a project name to create a completely new project 
                  that preserves all the original styling, including custom color palettes.
                </li>
                <li>
                  <strong>Handle style differences</strong> - If adding to an existing project with different 
                  styles, you'll see a warning about potential style mismatches before proceeding.
                </li>
                <li>
                  <strong>Track what you've copied</strong> - Items you've already copied show a "Copied" 
                  badge so you can easily see what you've already duplicated.
                </li>
              </ul>

              <h3 className="font-semibold mb-3 text-lg">Important Things to Know</h3>
              <ul className="list-disc pl-5 space-y-3 mb-6">
                <li>
                  <strong>Enterprise-only security</strong> - Content you share is only visible to members of 
                  your enterprise account, never to outside users.
                </li>
                <li>
                  <strong>When content becomes unavailable</strong> - If your enterprise expires or if the 
                  original creator deletes their project, shared items become inaccessible.
                </li>
                <li>
                  <strong>Version-specific sharing</strong> - When someone shares a specific version of their 
                  design, you'll only see that version, not their entire project history.
                </li>
                <li>
                  <strong>Read-only until copied</strong> - You can view shared content but cannot edit it 
                  unless you copy it to your own projects first.
                </li>
              </ul>

              <div className="bg-blue-50 p-4 rounded-lg mt-4">
                <p className="text-sm">
                  <strong>Pro Tip:</strong> Enterprise sharing is perfect for design reviews, getting feedback 
                  from stakeholders, and sharing design systems across your team. Since shares never expire, 
                  you can reference shared designs anytime as long as your enterprise account is active.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Clock className="w-5 h-5 mr-2 text-gray-700" />
              Enterprise Account Lifecycle
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Understanding the enterprise account lifecycle helps with long-term planning:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Active period</strong> - All team members have full access during the 365-day active period.
                </li>
                <li>
                  <strong>Expiration approach</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Admins receive notifications as the expiration date approaches</li>
                    <li>The enterprise dashboard shows the remaining time until expiration</li>
                  </ul>
                </li>
                <li>
                  <strong>After expiration</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>All team members lose generation capabilities</li>
                    <li>Projects and designs remain visible but can't be modified</li>
                    <li>The enterprise stays intact with all members still listed</li>
                  </ul>
                </li>
                <li>
                  <strong>Renewal</strong>:
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>The admin must purchase a new enterprise plan to restore access</li>
                    <li>Team composition is preserved during renewal</li>
                    <li>All existing projects become available for editing again</li>
                  </ul>
                </li>
              </ul>
              <p className="mt-4">
                Consider purchasing a renewal slightly before expiration to ensure uninterrupted access 
                for your team, especially during critical project phases.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              Best Practices for Enterprise Admins
            </h2>
            <div className="text-gray-700">
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Start with the right seat count</strong> - Choose a seat count that accounts for potential team growth during the year.</li>
                <li><strong>Document your enterprise key</strong> - Store your enterprise key securely for future reference.</li>
                <li><strong>Create a clear join process</strong> - Establish instructions for new team members on how to join.</li>
                <li><strong>Monitor seat usage</strong> - Regularly check your dashboard to ensure seats aren't being wasted.</li>
                <li><strong>Revoke access promptly</strong> - When team members leave, immediately revoke their access to free up seats.</li>
                <li><strong>Plan for renewal</strong> - Mark your calendar 30 days before expiration to prepare for renewal.</li>
                <li><strong>Consider project ownership</strong> - Important projects should be created by users who will remain with the organization long-term.</li>
                <li><strong>Export critical designs</strong> - Regularly export important designs as HTML for safekeeping.</li>
              </ul>
            </div>
          </section>
        </div>
      </div>
      <MainFooter />
    </div>
  );
};

export default EnterpriseGuide;